import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Check, X, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const emptyForm = { name: "", rating: 5, text: "" };
const inputCls =
  "w-full bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300";

export const ReviewsPanel = () => {
  const { t } = useLang();
  const rv = t.dash.reviews;
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setReviews((await api.get("/testimonials")).data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return;
    if (editing === "new") await api.post("/testimonials", form);
    else await api.patch(`/testimonials/${editing}`, form);
    setEditing(null);
    setForm(emptyForm);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/testimonials/${id}`);
    setReviews((rs) => rs.filter((x) => x.id !== id));
  };

  const renderForm = () => (
    <div className="px-5 py-4 border-b border-white/5 bg-white/[0.02]" data-testid="review-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={rv.name} className={inputCls} data-testid="review-form-name" />
        <div className="flex items-center gap-1 px-3" data-testid="review-form-rating">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-form-star-${i}`}>
              <Star className={`w-5 h-5 ${i <= form.rating ? "text-[#FBBC04] fill-[#FBBC04]" : "text-white/20"}`} />
            </button>
          ))}
        </div>
      </div>
      <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder={rv.text} rows={3} className={`${inputCls} resize-none`} data-testid="review-form-text" />
      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} data-testid="review-form-save" className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300">
          <Check className="w-3.5 h-3.5" /> {rv.save}
        </button>
        <button
          onClick={() => {
            setEditing(null);
            setForm(emptyForm);
          }}
          data-testid="review-form-cancel"
          className="inline-flex items-center gap-2 border border-white/15 text-white/60 hover:text-white text-xs px-4 py-2 rounded-full transition-colors duration-300"
        >
          <X className="w-3.5 h-3.5" /> {rv.cancel}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reviews-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white/40">{rv.subtitle}</p>
        <button
          onClick={() => {
            setEditing("new");
            setForm(emptyForm);
          }}
          data-testid="review-add-button"
          className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-xs px-4 py-2 rounded-full transition-colors duration-300"
        >
          <Plus className="w-3.5 h-3.5" /> {rv.add}
        </button>
      </div>
      <div className="border border-white/10 bg-[#111111]">
        {editing === "new" && renderForm()}
        {reviews.length === 0 && editing !== "new" && <p className="px-5 py-12 text-center text-sm text-white/40">{rv.empty}</p>}
        {reviews.map((r) =>
          editing === r.id ? (
            <div key={r.id}>{renderForm()}</div>
          ) : (
            <div key={r.id} className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/5 last:border-b-0" data-testid={`review-row-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-sm">{r.name}</p>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "text-[#FBBC04] fill-[#FBBC04]" : "text-white/20"}`} />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-white/50 mt-1 leading-relaxed">{r.text}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0 pt-0.5">
                <button
                  onClick={() => {
                    setEditing(r.id);
                    setForm({ name: r.name, rating: r.rating, text: r.text });
                  }}
                  data-testid={`review-edit-${r.id}`}
                  className="text-white/30 hover:text-[#FF7900] transition-colors duration-300"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} className="text-white/30 hover:text-red-400 transition-colors duration-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};
